import { getElapsedSeconds } from '@/shared/utils/time';
import { INITIAL_HEARTS } from '../types';

type LevelContext = { level_number: string; question_id: number };
type EventPayloads = {
	measurement_game_level_started: LevelContext & { playlist_section_id: number; hearts_at_start: number };
	measurement_game_level_completed: LevelContext & {
		playlist_section_id: number;
		level_duration_seconds: number;
		answer_attempts: number;
		hint_opened: boolean;
		stars_collected_in_level: number;
		hearts_at_end: number;
	};
	measurement_game_life_lost: LevelContext & {
		cause: 'fall' | 'incorrect_door_answer';
		hearts_after: number;
		answer_attempts: number;
	};
	measurement_game_hint_opened: LevelContext & { answer_attempts: number };
	measurement_game_npc_result: LevelContext & {
		result: 'fact_available' | 'not_enough_stars';
		next_threshold: number;
		stars_needed: number;
	};
	measurement_game_abandoned: LevelContext & {
		elapsed_seconds: number;
		levels_completed: number;
		hearts_remaining: number;
		playthrough_stars: number;
	};
};

export type MeasurementGameEvent = {
	[Name in keyof EventPayloads]: { event_name: Name; meta_data: EventPayloads[Name] };
}[keyof EventPayloads];

export type GameplayEvent =
	| { type: 'hearts_gained'; amount: number }
	| { type: 'life_lost'; cause: 'fall' | 'incorrect_door_answer'; hearts: number }
	| { type: 'hint' }
	| { type: 'stars'; value: number }
	| { type: 'npc'; result: 'fact_available' | 'not_enough_stars'; next_threshold: number; stars_needed: number };

export class Analytics {
	private context?: LevelContext;
	private levelIndex?: number;
	private levelStartedAt: number | null = null;
	private sessionStartedAt: number | null = null;
	private attempts = 0;
	private hintOpened = false;
	private levelStars = 0;
	private playthroughStars = 0;
	private levelsCompleted = 0;
	private hearts = INITIAL_HEARTS;
	private active = false;

	constructor(private emit: (event: MeasurementGameEvent) => void) {}

	start(startedAt = Date.now()) {
		this.context = undefined;
		this.levelIndex = undefined;
		this.sessionStartedAt = startedAt;
		this.levelsCompleted = 0;
		this.playthroughStars = 0;
		this.hearts = INITIAL_HEARTS;
		this.active = true;
	}

	startLevel(levelIndex: number, levelName: string, questionId: number, playlistSectionId: number) {
		if (!this.active || this.levelIndex === levelIndex) return;
		this.levelIndex = levelIndex;
		this.context = { level_number: levelName, question_id: questionId };
		this.levelStartedAt = Date.now();
		this.attempts = 0;
		this.hintOpened = false;
		this.levelStars = 0;
		this.emit({
			event_name: 'measurement_game_level_started',
			meta_data: { ...this.context, playlist_section_id: playlistSectionId, hearts_at_start: this.hearts },
		});
	}

	answer() {
		if (this.active) this.attempts += 1;
	}

	gameplay(event: GameplayEvent) {
		if (!this.active || !this.context) return;
		switch (event.type) {
			case 'hearts_gained':
				this.hearts = Math.min(INITIAL_HEARTS, this.hearts + event.amount);
				return;
			case 'stars':
				this.levelStars += event.value;
				this.playthroughStars += event.value;
				return;
			case 'life_lost':
				this.hearts = event.hearts;
				this.emit({
					event_name: 'measurement_game_life_lost',
					meta_data: {
						...this.context,
						cause: event.cause,
						hearts_after: this.hearts,
						answer_attempts: this.attempts,
					},
				});
				return;
			case 'hint':
				this.hintOpened = true;
				this.emit({
					event_name: 'measurement_game_hint_opened',
					meta_data: { ...this.context, answer_attempts: this.attempts },
				});
				return;
			case 'npc':
				this.emit({
					event_name: 'measurement_game_npc_result',
					meta_data: {
						...this.context,
						result: event.result,
						next_threshold: event.next_threshold,
						stars_needed: event.stars_needed,
					},
				});
		}
	}

	complete(playlistSectionId: number, reward: number) {
		if (!this.active || !this.context || this.levelStartedAt === null) return;
		this.playthroughStars += reward;
		this.levelsCompleted += 1;
		this.emit({
			event_name: 'measurement_game_level_completed',
			meta_data: {
				...this.context,
				playlist_section_id: playlistSectionId,
				level_duration_seconds: getElapsedSeconds(this.levelStartedAt),
				answer_attempts: this.attempts,
				hint_opened: this.hintOpened,
				stars_collected_in_level: this.levelStars,
				hearts_at_end: this.hearts,
			},
		});
		this.levelStartedAt = null;
	}

	finish() {
		this.active = false;
	}

	abandon() {
		if (!this.active || !this.context) return;
		this.active = false;
		this.emit({
			event_name: 'measurement_game_abandoned',
			meta_data: {
				...this.context,
				elapsed_seconds: getElapsedSeconds(this.sessionStartedAt),
				levels_completed: this.levelsCompleted,
				hearts_remaining: this.hearts,
				playthrough_stars: this.playthroughStars,
			},
		});
	}
}
